import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {createCustomer, getCustomerDetails, getCustomers} from 'src/api/server/customer';

// ----------------------------------------------------------------------

const CUSTOMER_PAGE_LIMIT = 500;

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getNextCustomerPage = (pagination = {}, requestedPage = 1, resultCount = 0) => {
  const currentPage = toNumber(pagination.page ?? pagination.currentPage) || requestedPage;
  const limit = toNumber(pagination.limit ?? pagination.pageSize ?? pagination.perPage) || CUSTOMER_PAGE_LIMIT;

  if (pagination.nextPage !== undefined && pagination.nextPage !== null) {
    const nextPage = toNumber(pagination.nextPage);
    return nextPage && nextPage > currentPage ? nextPage : null;
  }

  const totalPages = toNumber(pagination.totalPages ?? pagination.pages);
  if (totalPages && currentPage < totalPages) {
    return currentPage + 1;
  }

  const total = toNumber(
    pagination.total ?? pagination.totalItems ?? pagination.totalDocs ?? pagination.count
  );
  if (total && currentPage * limit < total) {
    return currentPage + 1;
  }

  return resultCount === limit && pagination.hasNextPage ? currentPage + 1 : null;
};

export const fetchCustomersData = createAsyncThunk(
  'customers/fetchCustomersData',
  async ({ idUser, config, search, page = 1, limit = CUSTOMER_PAGE_LIMIT }) => {
    const response = await getCustomers(idUser, config, search, page, limit);
    const data = response?.data ?? response ?? {};

    // const { customers, pagination } = response.data;
    // await Promise.all((customers || []).map((customer) => addData(customer)));

    const customers = data.customers || data.data || data.results || [];
    const pagination = data.pagination || {};
    const results = (Array.isArray(customers) ? customers : []).map(c => ({...c, id: c._id || c.id }))

    return {
      results,
      nextPage: getNextCustomerPage(pagination, page, results.length),
      page,
      totalPages: toNumber(pagination.totalPages ?? pagination.pages),
      total: toNumber(
        pagination.total ?? pagination.totalItems ?? pagination.totalDocs ?? pagination.count
      ),
      limit,
    }
  });

export const fetchOneCustomer = createAsyncThunk(
  'customers/fetchOneCustomer',
  async ({ idUser, config, id }) => {
    const response = await getCustomerDetails(idUser, config, id);
    return response.data;
  });

export const addNewCustomer = createAsyncThunk(
  'customers/addNewCustomer',
  async ({ idUser, config, data }) => {
    const response = await createCustomer(idUser, config, data);
    return response.data;
  });

const initialState = {
  allData: [],
  isLoading: false,
  error: undefined,
  nextPage : 1,
  total: 0,
  totalPages: 1,
};

const slice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    resetCustomers: (state) => {
      Object.entries(initialState).forEach(([key, value]) => {
        state[key] = value;
      });
    },
    updateCustomer: (state, action) => {
      const customer = action.payload;
      if (!customer) return;

      const customerFound = state.allData.find(
        x => (customer.id && x?.id === customer.id) || (customer.email && x?.email === customer.email)
      );

      const customerData = {
        id: customer.id,
        email: customer.email,
        idERP: customer.idERP,
        lastName: customer.lastName,
        firstName: customer.firstName,
        phone: customer.phone,
        address: customer.address,
        isActive: customer.isActive,
        notes: customer.notes,
        // Include vouchers and transactions if present (map server fields to frontend format)
        vouchers: customer.vouchers?.map(v => ({
          ...v,
          id: v.id || v._id,
          totalAmount: v.totalAmount || v.initialAmount || 0,
          remainingAmount: v.remainingAmount || v.currentAmount || 0,
          usedAmount: v.usedAmount || (v.initialAmount - v.currentAmount) || 0,
          customerId: v.customer?.id || v.customer,
          createdAt: v.createdAt || v.created_at,
          expiresAt: v.expiresAt || v.expiryDate,
        })),
        transactions: customer.transactions,
      };

      if (!customerFound) {
        // Ajouter le nouveau client
        customerData.importedAt = (new Date()).toLocaleString();
        customerData.lastUpdatedDate = (new Date()).toLocaleString();
        state.allData.push(customerData);
      } else {
        // Mettre à jour le client existant
        Object.entries(customerData).forEach(([key, value]) => {
          if (value !== undefined) {
            customerFound[key] = value;
          }
        });
        customerFound.lastUpdatedDate = (new Date()).toLocaleString();
      }
    },
    updateCustomers: (state, action) => {
      const { data } = action.payload;
      data.forEach(customer => {
        const customerFound = state.allData.find(curr => curr.id === customer?.id || curr.email === customer?.email);
        if (customerFound) {
          // Mettre à jour le client existant
          Object.entries(customer).forEach(([key, value]) => {
            customerFound[key] = value;
          });
        } else {
          // Ajouter le nouveau client
          const customerToAdd = {
            ...customer,
            importedAt: (new Date()).toLocaleString(),
            lastUpdatedDate: (new Date()).toLocaleString(),
          };
          state.allData.push(customerToAdd);
        }
      });
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCustomersData.pending, state => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(fetchCustomersData.fulfilled, (state, action) => {
        const { nextPage, results, total, totalPages } = action.payload
        state.isLoading = false
        state.error = undefined
        state.nextPage = nextPage
        state.total = total ?? state.total
        state.totalPages = totalPages ?? state.totalPages
        const customersUpdated = results.map(customer => {
          const existingCustomer = state.allData.find(x => x?.id === customer.id || x?.email === customer.email) || {}

          const customerToAdd = {
            id: existingCustomer?.id || customer.id,
            email: existingCustomer?.email || customer.email,
            idERP: customer.idERP,
            lastName: customer.lastName,
            firstName: customer.firstName,
            phone: customer.phone,
            phoneNumber: customer.phone,
            birthDate: customer.birthDate,
            preferredLanguage: customer.preferredLanguage,
            customerSince: customer.customerSince,
            marketingConsent: customer.marketingConsent,
            address: customer.address,
            isActive: customer.isActive,
            notes: customer.notes,
            // Include vouchers and transactions from API response (map server fields to frontend format)
            vouchers: customer.vouchers?.map(v => ({
              ...v,
              id: v.id || v._id,
              totalAmount: v.totalAmount || v.initialAmount || 0,
              remainingAmount: v.remainingAmount || v.currentAmount || 0,
              usedAmount: v.usedAmount || (v.initialAmount - v.currentAmount) || 0,
              customerId: v.customer?.id || v.customer,
              createdAt: v.createdAt || v.created_at,
              expiresAt: v.expiresAt || v.expiryDate,
            })),
            transactions: customer.transactions,
          }

          return { ...existingCustomer, ...customerToAdd, importedAt: existingCustomer.importedAt || (new Date()).toLocaleString() }
        })
        const existingCustomers = state.allData.filter(
          (c) => !results.some((nc) => nc.id === c.id || nc.email === c.email)
        )
        state.allData = [...customersUpdated, ...existingCustomers]
      })
      .addCase(fetchCustomersData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchOneCustomer.fulfilled, (state, action) => {
        const data = action.payload;
        const dataFound = state.allData.find(x => x?.id === data.id || x?.email === data.email);
        const dataToPush = {
          id: data.id,
          email: data.email,
          idERP: data.idERP,
          lastName: data.lastName,
          firstName: data.firstName,
          phone: data.phone,
          address: data.address,
          company: data.company,
          isCompany: data.isCompany,
          isActive: data.isActive,
          notes: data.notes,
          // Include vouchers and transactions from API response (map server fields to frontend format)
          vouchers: data.vouchers?.map(v => ({
            ...v,
            id: v.id || v._id,
            totalAmount: v.totalAmount || v.initialAmount || 0,
            remainingAmount: v.remainingAmount || v.currentAmount || 0,
            usedAmount: v.usedAmount || (v.initialAmount - v.currentAmount) || 0,
            customerId: v.customer?.id || v.customer,
            createdAt: v.createdAt || v.created_at,
            expiresAt: v.expiresAt || v.expiryDate,
          })),
          transactions: data.transactions,
        }
        if (!dataFound) {
          dataToPush.importedAt = (new Date()).toLocaleString();
          dataToPush.lastUpdatedDate = (new Date()).toLocaleString();
          state.allData.push(dataToPush);
        } else {
          Object.entries(dataToPush).forEach(entry => {
            const [key, value] = entry;
            if (key !== 'importedAt' && key !== 'lastUpdatedDate' && value) dataFound[key] = value;
          });
          dataFound.lastUpdatedDate = (new Date()).toLocaleString();
        }
      })
      .addCase(addNewCustomer.fulfilled, (state, action) => {
        const customer = action.payload;
        const customerFound = state.allData.find(x => x?.id === customer.id || x?.email === customer.email);
        if (!customerFound) {
          const customerToAdd = {
            id: customer.id,
            email: customer.email,
            idERP: customer.idERP,
            lastName: customer.lastName,
            firstName: customer.firstName,
            phone: customer.phone,
            address: customer.address,
            isActive: customer.isActive,
            notes: customer.notes,
            importedAt: (new Date()).toLocaleString(),
            lastUpdatedDate: (new Date()).toLocaleString(),
          };
          state.allData.push(customerToAdd);
        }
      });
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  resetCustomers,
  updateCustomers,
  updateCustomer,
} = slice.actions;
